import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Output, OnInit, ChangeDetectorRef, OnDestroy, HostListener } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PickerComponent } from '@ctrl/ngx-emoji-mart';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { ChatbotService } from 'src/app/shared/services/chatbot/chatbot.service';
import {
	ChatMessageUI,
	ChatAttachmentUI,
	ChatActionUI,
	ChatConnectionState
} from 'src/app/shared/services/chatbot/chatbot.models';
import { ConcatenateSrcDirective } from '@shared/directives/concatenate-src/concatenate-src.directive';

@Component({
	selector: 'app-modal-chatbot',
	standalone: true,
	imports: [CommonModule, FormsModule, PickerComponent, ConcatenateSrcDirective],
	templateUrl: './modal-chatbot.component.html',
	styleUrl: './modal-chatbot.component.scss'
})
export class ModalChatbotComponent implements OnInit, OnDestroy {
	constructor(
		private cdr: ChangeDetectorRef,
		private router: Router,
		private chatbotService: ChatbotService
	) {}

	@Output() closed = new EventEmitter<void>();
	@Output() back = new EventEmitter<void>();

	message = '';
	showEmojiPicker = false;
	isRecording = false;
	mediaRecorder?: MediaRecorder;
	audioChunks: Blob[] = [];

	pendingAttachment: ChatAttachmentUI | null = null;
	connectionState: ChatConnectionState = 'disconnected';

	messages: ChatMessageUI[] = [];
	replyingToMessage: ChatMessageUI | null = null;
	private messagesSub!: Subscription;
	private stateSub!: Subscription;

	closeModal(): void {
		this.closed.emit();
	}

	goToFullChat(): void {
		this.closed.emit();
		this.router.navigate(['/profile/ambassador/chat-socio']);
	}

	ngOnInit(): void {
		this.messagesSub = this.chatbotService.messages$.subscribe((msgs) => {
			const wasNearBottom = this.isUserNearBottom();
			const isInitialLoad = this.messages.length === 0 && msgs.length > 0;
			
			this.messages = msgs;
			this.cdr.detectChanges();
			
			if (wasNearBottom || isInitialLoad) {
				this.forceScroll();
			}
		});

		this.stateSub = this.chatbotService.connectionState$.subscribe((state) => {
			this.connectionState = state;
			this.cdr.detectChanges();
		});

		this.chatbotService.connect();
	}

	ngOnDestroy(): void {
		if (this.messagesSub) this.messagesSub.unsubscribe();
		if (this.stateSub) this.stateSub.unsubscribe();
	}

	shouldShowDateSeparator(index: number): boolean {
		if (index === 0) return true;
		const current = this.messages[index];
		const previous = this.messages[index - 1];
		if (!current.dateObj || !previous.dateObj) return false;

		return current.dateObj.toDateString() !== previous.dateObj.toDateString();
	}

	getDateSeparatorText(dateObj?: Date): string {
		if (!dateObj) return '';
		const today = new Date();
		const yesterday = new Date();
		yesterday.setDate(yesterday.getDate() - 1);

		if (dateObj.toDateString() === today.toDateString()) return 'Hoy';
		if (dateObj.toDateString() === yesterday.toDateString()) return 'Ayer';

		return dateObj.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
	}

	formatText(text?: string): string {
		if (!text) return '';
		let formatted = text.replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank">$1</a>');
		formatted = formatted.replace(/\n/g, '<br>');
		return formatted;
	}

	handleAction(action: ChatActionUI, contextId?: string): void {
		this.sendDirectMessage(action.title, action.id, contextId);
	}

		private isUserNearBottom(): boolean {
		const container = document.querySelector('.inchat-window__messages');
		if (!container) return true;
		const threshold = 150; // pixels
		const position = container.scrollTop + container.clientHeight;
		const height = container.scrollHeight;
		return height - position <= threshold;
	}


		async downloadAttachment(attachment: any): Promise<void> {
		if (!attachment || !attachment.url) return;
		try {
			const response = await fetch(attachment.url);
			if (!response.ok) throw new Error('Network response was not ok');
			const blob = await response.blob();
			const blobUrl = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = blobUrl;
			a.download = attachment.name || 'descarga';
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			window.URL.revokeObjectURL(blobUrl);
		} catch (error) {
			console.error('CORS o red fallida, abriendo en nueva pestaña:', error);
			window.open(attachment.url, '_blank');
		}
	}

	private forceScroll(): void {
		setTimeout(() => {
			const messagesContainer = document.querySelector('.inchat-window__messages');
			if (messagesContainer) {
				messagesContainer.scrollTop = messagesContainer.scrollHeight;
			}
		}, 0);
	}

	clearAttachment(): void {
		this.pendingAttachment = null;
		this.cdr.detectChanges();
	}

	private async sendDirectMessage(optimisticTitle: string, backendPayload: string, contextId: string = ''): Promise<void> {
		if (this.connectionState === 'error') return;

		const tempId = Date.now().toString();
		const tempMessage: ChatMessageUI = {
			id: tempId,
			sender: 'user',
			text: optimisticTitle,
			time: this.time,
			status: 'sending',
			optimistic: true,
			dateObj: new Date(),
			backendPayload: backendPayload
		};

		this.chatbotService.appendOptimisticMessage(tempMessage);
		this.message = '';

		try {
			await this.chatbotService.sendMessage(backendPayload, '', contextId);
			this.chatbotService.updateMessageStatus(tempId, 'sent');
		} catch (error) {
			this.chatbotService.updateMessageStatus(tempId, 'error');
		}
	}

	async sendMessage(): Promise<void> {
		const replying = this.replyingToMessage;
		const contextId = replying?.id || '';
		let contextText = replying?.text || '';
		if (!contextText && replying?.attachment) {
			contextText = replying.attachment.name || 'Archivo adjunto';
		}
		if (!contextText && contextId) {
			contextText = 'Mensaje citado';
		}
		const contextName = replying ? (replying.sender === 'user' ? 'Tú' : 'Soporte') : '';

		if (this.connectionState === 'error') return;

		const text = this.message.trim();
		const attachment = this.pendingAttachment;

		if (!text && !attachment) return;

		const tempId = Date.now().toString();
		const tempMessage: ChatMessageUI = {
			id: tempId,
			sender: 'user',
			text: text,
			time: this.time,
			status: 'sending',
			attachment: attachment ? { ...attachment } : undefined,
			optimistic: true,
			dateObj: new Date(),
			contextId: contextId || undefined,
			contextText: contextText || undefined,
			contextName: contextName || undefined
		};

		this.chatbotService.appendOptimisticMessage(tempMessage);

		this.message = '';
		this.pendingAttachment = null;
		this.replyingToMessage = null;

		setTimeout(() => {
			const textarea = document.querySelector('.inchat-window__input textarea') as HTMLTextAreaElement;
			if (textarea) textarea.style.height = 'auto';
		}, 0);

		try {
			let fileUrl = '';

			if (attachment && attachment.file) {
				fileUrl = await this.chatbotService.uploadFile(attachment.file);
			}

			await this.chatbotService.sendMessage(text, fileUrl, contextId);
			this.chatbotService.updateMessageStatus(tempId, 'sent');
		} catch (error) {
			this.chatbotService.updateMessageStatus(tempId, 'error');
		}
	}

	replyToMessage(item: ChatMessageUI): void {
		this.replyingToMessage = item;
	}

	cancelReply(): void {
		this.replyingToMessage = null;
	}

	get time(): string {
		return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	}

	toggleEmojiPicker(event?: MouseEvent): void {
		if (event) {
			event.stopPropagation();
		}
		if (this.connectionState === 'error') return;
		this.showEmojiPicker = !this.showEmojiPicker;
	}

	addEmoji(event: any): void {
		if (this.connectionState === 'error') return;
		this.message += event.emoji.native;
		this.showEmojiPicker = false;
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent): void {
		if (!this.showEmojiPicker) return;
		const target = event.target as HTMLElement;
		const clickedInsidePicker = target.closest('.emoji-picker-container');
		const clickedEmojiButton = target.closest('[aria-label="Emoji"]') || target.closest('.inchat-icon--emoji');
		if (!clickedInsidePicker && !clickedEmojiButton) {
			this.showEmojiPicker = false;
		}
	}

	adjustHeight(event: Event): void {
		const textarea = event.target as HTMLTextAreaElement;
		textarea.style.height = 'auto';
		textarea.style.height = `${Math.min(textarea.scrollHeight, 92)}px`;
	}

	onKeyDown(event: KeyboardEvent): void {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			this.sendMessage();
		}
	}

	onFileSelected(event: Event): void {
		if (this.connectionState === 'error') return;

		const input = event.target as HTMLInputElement;
		if (input.files && input.files.length > 0) {
			const file = input.files[0];

			if (file.type.startsWith('audio/') || file.type.startsWith('video/')) {
				alert('Formato no soportado. Por favor, selecciona solo imágenes o documentos.');
				input.value = '';
				return;
			}

			const isImage = file.type.startsWith('image/');
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onloadend = () => {
				this.pendingAttachment = {
					name: file.name,
					type: isImage ? 'image' : 'document',
					url: reader.result as string,
					file: file
				};

				setTimeout(() => {
					const textarea = document.querySelector(
						'.inchat-window__input textarea'
					) as HTMLTextAreaElement;
					if (textarea) textarea.focus();
				}, 0);

				this.cdr.detectChanges();
			};
			input.value = '';
		}
	}

	async toggleRecording(): Promise<void> {
		if (this.connectionState === 'error') return;

		if (this.isRecording) {
			this.mediaRecorder?.stop();
			this.isRecording = false;
		} else {
			try {
				const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
				this.mediaRecorder = new MediaRecorder(stream);
				this.audioChunks = [];

				this.mediaRecorder.ondataavailable = (event) => {
					if (event.data.size > 0) this.audioChunks.push(event.data);
				};

				this.mediaRecorder.onstop = () => {
					const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
					const reader = new FileReader();
					reader.readAsDataURL(audioBlob);
					reader.onloadend = () => {
						const base64Url = reader.result as string;
						this.pendingAttachment = {
							name: 'Audio grabado',
							type: 'audio',
							url: base64Url,
							file: new File([audioBlob], 'audio.webm', { type: 'audio/webm' })
						};
						this.cdr.detectChanges();
					};
					stream.getTracks().forEach((track) => track.stop());
				};

				this.mediaRecorder.start();
				this.isRecording = true;
			} catch (err) {
				alert('No se pudo acceder al micrófono.');
			}
		}
	}
}
